import { useState } from "react";
import { 
  Sparkles, 
  Wand2, 
  Check, 
  X, 
  Loader2,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import { useApi } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Field type mappings for display
const FIELD_TYPE_LABELS = {
  text: "Text",
  list: "List",
  pros: "PRO List",
  fee_structure: "Fee Structure",
  number: "Number",
  date: "Date",
  status: "Status"
};

// Confidence badge colors
const CONFIDENCE_COLORS = {
  high: "bg-blue-100 text-blue-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-red-100 text-red-700"
};

export default function EnrichmentPopup({
  isOpen,
  onClose,
  fieldType = "text",
  fieldName,
  currentValue,
  countryCode,
  category,
  onAccept
}) {
  const api = useApi();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleEnrich = async () => {
    if (!prompt.trim()) {
      toast.error("Please describe what data you need");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.post(`/api/enrichment/field`, {
        field_type: fieldType,
        field_name: fieldName,
        current_value: currentValue,
        user_prompt: prompt,
        country_code: countryCode,
        category: category
      });

      if (response.data.success) {
        setResult(response.data);
      } else {
        toast.error(response.data.error || "Failed to get enrichment");
      }
    } catch (error) {
      console.error("Enrichment error:", error);
      toast.error(error.response?.data?.detail || "Failed to enrich data");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (result?.suggested_value) {
      onAccept(result.suggested_value);
      toast.success("Data applied successfully");
      handleClose();
    }
  };

  const handleClose = () => {
    setPrompt("");
    setResult(null);
    setShowDetails(false);
    onClose();
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    
    if (Array.isArray(value)) {
      if (value.length === 0) return "Empty list";
      if (typeof value[0] === "object") {
        // PRO list or similar
        return (
          <div className="space-y-1">
            {value.map((item, idx) => (
              <div key={idx} className="text-xs bg-zinc-50 p-1.5 rounded">
                {item.name && <span className="font-medium">{item.name}</span>}
                {item.website && <span className="text-zinc-500 ml-2">{item.website}</span>}
              </div>
            ))}
          </div>
        );
      }
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">{item}</Badge>
          ))}
        </div>
      );
    }
    
    if (typeof value === "object") {
      return (
        <pre className="text-xs bg-zinc-50 p-2 rounded overflow-auto max-h-40">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    
    return <span className="text-sm">{String(value)}</span>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500" />
            AI Data Enrichment
          </DialogTitle>
          <DialogDescription>
            Use AI to help fill in {fieldName} based on EPR knowledge base
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Context Info */}
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">
              Field: {fieldName}
            </Badge>
            <Badge variant="outline">
              Type: {FIELD_TYPE_LABELS[fieldType] || fieldType}
            </Badge>
            {countryCode && (
              <Badge variant="outline">
                Country: {countryCode}
              </Badge>
            )}
            {category && (
              <Badge variant="outline">
                Category: {category}
              </Badge>
            )}
          </div>

          {/* Current Value */}
          {currentValue && (
            <div className="p-3 bg-zinc-50 rounded-sm">
              <div className="text-xs text-zinc-500 mb-1">Current Value:</div>
              {renderValue(currentValue)}
            </div>
          )}

          {/* User Prompt */}
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1.5 block">
              Describe what data you need:
            </label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={`E.g., "Find the official regulation name for ${category || 'packaging'} EPR in ${countryCode || 'this country'}" or "List the main PROs for this waste stream"`}
              rows={3}
              className="text-sm"
              data-testid="enrichment-prompt"
            />
          </div>

          {/* Enrich Button */}
          <Button 
            onClick={handleEnrich} 
            disabled={loading || !prompt.trim()}
            className="w-full"
            data-testid="enrich-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                Get AI Suggestion
              </>
            )}
          </Button>

          {/* Result */}
          {result && (
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200 animate-fade-in">
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-medium text-stone-800 text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-500" />
                  AI Suggestion
                </h4>
                <Badge className={`text-xs ${CONFIDENCE_COLORS[result.confidence] || CONFIDENCE_COLORS.medium}`}>
                  {result.confidence} confidence
                </Badge>
              </div>

              {/* Suggested Value */}
              <div className="mb-3 p-3 bg-white rounded-lg border border-blue-100">
                <div className="text-xs text-stone-500 mb-1.5">Suggested Value:</div>
                {renderValue(result.suggested_value)}
              </div>

              {/* Explanation */}
              {result.explanation && (
                <div className="mb-3">
                  <div className="flex items-center gap-1 text-xs text-stone-500 mb-1">
                    <Info className="w-3 h-3" />
                    Explanation:
                  </div>
                  <p className="text-sm text-stone-700">{result.explanation}</p>
                </div>
              )}

              {/* Sources (Collapsible) */}
              {result.sources && result.sources.length > 0 && (
                <Collapsible open={showDetails} onOpenChange={setShowDetails}>
                  <CollapsibleTrigger className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
                    {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {showDetails ? "Hide" : "Show"} sources ({result.sources.length})
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <ul className="text-xs text-stone-600 space-y-1">
                      {result.sources.map((source, idx) => (
                        <li key={idx} className="flex items-start gap-1">
                          <span className="text-blue-500">•</span>
                          {source}
                        </li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Accept/Reject Buttons */}
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={handleAccept}
                  className="flex-1"
                  data-testid="accept-enrichment"
                >
                  <Check className="w-4 h-4 mr-1.5" />
                  Accept & Apply
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setResult(null)}
                  data-testid="reject-enrichment"
                >
                  <X className="w-4 h-4 mr-1.5" />
                  Reject
                </Button>
              </div>
            </div>
          )}

          {/* Warning for low confidence */}
          {result?.confidence === "low" && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
              <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium text-amber-800">Low confidence suggestion</span>
                <p className="text-amber-700 text-xs mt-0.5">
                  This data may not be fully accurate. Please verify before accepting.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Trigger button component for easy integration
export function EnrichButton({ onClick, size = "sm" }) {
  return (
    <Button
      variant="ghost"
      size={size}
      onClick={onClick}
      className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
      title="AI Enrich"
      data-testid="ai-enrich-trigger"
    >
      <Sparkles className="w-3.5 h-3.5" />
    </Button>
  );
}
